<template>
  <div
    v-if="mode === 'schedule'"
    class="flex flex-col bg-white rounded-2xl p-4 md:hover:shadow-sm border border-gray-100 shadow-sm cursor-pointer"
    @click="$emit('chooseService', service)"
  >
    <div class="flex flex-row justify-between items-center gap-3">
      <div class="flex flex-row items-start flex-1">
        <img
          v-if="service?.imageUrl"
          :src="service?.imageUrl"
          alt="service image"
          :class="['me-3 rounded-xl object-cover flex-none', size==='small' ? 'w-12 h-12' : 'w-14 h-14']">
        <div class="flex flex-col items-start">
          <span :class="['font-medium leading-snug', size === 'small' ? 'text-sm' : 'text-base']">{{ service?.name }}</span>
          <span v-if="service?.description" class="text-gray-500 text-xs mt-1 leading-snug">{{ service.description }}</span>
        </div>
      </div>
      <div v-wave
           :class="['border rounded-full flex justify-center items-center px-4 py-2 flex-none transition-colors',
                    service.serviceType === 'complementary' ? 'min-w-[6rem]' : 'min-w-[4.5rem]']"
           :style="{
        background: isSelected ? colors.leadColor : 'transparent',
        borderColor: colors.darkenColor,
        color: isSelected ? colors.fontColor : colors.darkenColor
      }"
      >
        <span v-if="service.serviceType === 'complementary'" class="text-xs whitespace-nowrap">
          {{isSelected ? t('serviceItem.removeFromAppointment') : t('serviceItem.addToAppointment')}}
        </span>
        <span v-else class="text-sm whitespace-nowrap">
          {{isSelected ? t('common.cancel') : t('serviceItem.bookAppointment')}}
        </span>
      </div>
    </div>
    <div class="mt-3 font-medium text-sm text-black self-start flex items-center gap-1.5" style="direction: ltr">
      <span v-if="isDiscounted" class="text-[10px] font-medium text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded-full">{{ t('serviceItem.sale') }}</span>
      <span>{{ formattedPrice }}</span>
      <span v-if="discountOriginalLabel" class="text-gray-400 font-normal line-through">{{ discountOriginalLabel }}</span>
      <span v-if="durationLabel" class="text-gray-400 font-normal">· {{ durationLabel }}</span>
    </div>
  </div>

  <div
    v-else
    class="flex flex-col rounded-lg md:border md:p-4 md:hover:bg-gray-50 md:cursor-pointer"
  >
    <div class="flex flex-row justify-between items-center">
      <div class="flex flex-row justify-between items-start flex-1">
        <img
          v-if="service?.imageUrl"
          :src="service?.imageUrl"
          alt="service image"
          :class="['me-3 rounded-xl', size==='small' ? 'w-14 h-14' : 'w-20 h-20 ']">
        <div class="flex w-full flex-col h-full items-start">
          <span :class="['font-medium', size === 'small' ? 'text-sm' : '']">{{ service?.name }}</span>
          <span :class="['text-gray-500', size === 'small' ? 'text-xs' : 'text-sm']">{{ service?.description }}</span>
          <span v-if="mode !== 'payment'"
                :class="['flex-none mt-auto pt-2', size === 'small' ? 'text-sm' : '']"
                style="direction: ltr">
            {{ formattedPrice }}
            <span v-if="discountOriginalLabel" class="text-gray-400 line-through ms-1">{{ discountOriginalLabel }}</span>
          </span>
        </div>
      </div>

      <div v-if="mode==='checkout'"
           @click="$emit('removeService', service)"
           class="aspect-square !w-8 !h-8 flex justify-center items-center"
      >
        <i class="pi pi-trash !text-sm text-red-500" />
      </div>

      <span v-if="mode === 'payment'" :class="['flex-none my-auto ', size === 'small' ? 'text-sm' : '']" style="direction: ltr">
        <span v-if="discountOriginalLabel" class="text-gray-400 line-through me-1">{{ discountOriginalLabel }}</span>
        {{ formattedPrice }}
      </span>
    </div>

    <div v-if="service?.notes" class="service-note">
      <i class="pi pi-info-circle service-note__icon" />
      <span class="service-note__text">{{ service.notes }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import { IService } from '@torim-app/types';
import { formatServicePrice, providerOverrideFor, resolveServiceDuration } from '@torim-app/utils';

const fallbackStrings: Record<string, string> = {
  'common.free': 'ללא עלות',
  'common.cancel': 'ביטול',
  'serviceItem.bookAppointment': 'קביעת תור',
  'serviceItem.addToAppointment': 'הוספה לתור',
  'serviceItem.removeFromAppointment': 'הסרה מהתור',
  'serviceItem.minutesShort': 'דקות',
  'serviceItem.steps': 'שלבים',
  'serviceItem.sale': 'מבצע',
};

let t: (key: string) => string;
try {
  t = useI18n().t;
} catch {
  t = (key: string) => fallbackStrings[key] ?? key;
}

const props = defineProps({
  service: {
    type: Object as PropType<IService>,
    required: true
  },
  selectedServices: {
    type: Array as PropType<IService[]>,
    required: false,
    default: () => []
  },
  mode: {
    type: String as PropType<'checkout' | 'schedule' | 'payment'>,
    required: false,
    default: 'schedule'
  },
  animationDelay: {
    type: Number,
    required: false,
    default: 0
  },
  size: {
    type: String as PropType<'small' | 'normal' >,
    required: false,
    default: 'normal'
  },
  colors: {
    type: Object as PropType<{
      leadColor: string;
      fontColor: string;
      darkenColor: string;
    }>,
    required: false,
    default: () => ({
      leadColor: '#000000',
      fontColor: '#000000',
      darkenColor: '#000000',
    })
  },
  // When a specific provider is chosen, show that provider's resolved price instead of the
  // cross-provider range.
  providerId: {
    type: String,
    required: false,
    default: undefined
  }
});

defineEmits(['chooseService', 'removeService']);

const isSelected = computed(() => {
  return props.selectedServices.find(x => x.id === props.service?.id);
});

const formattedPrice = computed(() => formatServicePrice(props.service, { freeLabel: t('common.free'), providerId: props.providerId && props.providerId !== 'any' ? props.providerId : undefined }));

// A service is "on sale" when it has a discountedPrice below its list price AND the chosen
// provider doesn't have its own price override (a provider price replaces, not discounts).
const isDiscounted = computed(() => {
  const s = props.service;
  const pid = props.providerId && props.providerId !== 'any' ? props.providerId : undefined;
  if (pid && providerOverrideFor(s, pid)?.price != null) return false;
  return typeof s?.discountedPrice === 'number' && s.discountedPrice < s.price;
});

// The pre-discount (list) price, shown struck through next to the sale price.
const discountOriginalLabel = computed(() => (isDiscounted.value ? `₪${props.service.price}` : ''));

const durationLabel = computed(() => {
  const duration = props.service ? resolveServiceDuration(props.service) : 0;
  return duration ? `${duration} ${t('serviceItem.minutesShort')}` : '';
});

</script>

<style scoped lang="scss">
.service-note {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.625rem;
  padding: 0.5rem 0.75rem;
  background: #fff7ed; // amber-50
  border: 1px solid #fed7aa; // amber-200
  border-radius: 0.625rem;
  color: #9a3412; // amber-800
  font-size: 0.8125rem;
  line-height: 1.4;

  &__icon {
    font-size: 0.9rem;
    margin-top: 1px;
    flex: none;
  }

  &__text {
    flex: 1;
    white-space: pre-line;
    word-break: break-word;
  }
}
</style>
